import React, { useState, useRef, useEffect } from 'react';
import { SERVICES } from '../data/seo';
import { Star, Scissors, Sparkles, ChevronDown } from 'lucide-react';
import HeroImage from './HeroImage';
import MaskedHeading from './MaskedHeading';
import { CATALOGUE } from '../data/catalogue';
import { HERO_CARD } from '../data/heroCard';
import { STUDIO } from '../data/studio';

const HEADLINE = [
  { text: 'Handmade bee', tone: 'light' },
  { text: 'Atelier', tone: 'accent' },
  { text: 'of', tone: 'accent' },
  { text: 'Crafts', tone: 'accent' },
];

// The photograph seen through the company name. A real piece from the
// catalogue, so what shows through the letters is the studio's own work.
const MASK_WORK = CATALOGUE.find((w) => w.id === 'frame-tawakkul-teal') || CATALOGUE[0];
const MASK_SRC = MASK_WORK.imageSet.map((r) => `${r.webp} ${r.w}w`).join(', ');

export default function Hero3D({ onExplore, onOpenQuote }) {
  const heroRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [motionOK, setMotionOK] = useState(false);

  // Parallax is pointer-driven, so it is dead weight without a real pointer.
  // Reduced-motion users opt out of all of it.
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hover = window.matchMedia('(hover: hover)');
    const sync = () => setMotionOK(!reduce.matches && hover.matches);
    sync();
    reduce.addEventListener('change', sync);
    hover.addEventListener('change', sync);
    return () => {
      reduce.removeEventListener('change', sync);
      hover.removeEventListener('change', sync);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (!heroRef.current || !motionOK) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 2,
    });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePos({ x: 0, y: 0 });
  };

  // Under 1 degree and under 16px of travel reads as expensive.
  // Past 3 degrees and 40px it reads as a template.
  const tiltCardStyle = {
    transform: isHovered && motionOK
      ? `perspective(1000px) rotateX(${-mousePos.y * 5}deg) rotateY(${mousePos.x * 5}deg) scale3d(1.008, 1.008, 1.008)`
      : 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
    transition: isHovered ? 'transform 0.14s ease-out' : 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
  };

  const layerStyle = (depth) => ({
    transform: `translate3d(${(mousePos.x * -14 * depth).toFixed(2)}px, ${(mousePos.y * -10 * depth).toFixed(2)}px, 0)`,
    transition: isHovered ? 'transform 0.14s ease-out' : 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
  });

  return (
    <section
      ref={heroRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden hero-palette-gradient text-[#FFF7F0]"
    >
      {/* Warm ambient glow, drifting against the pointer */}
      <div
        className="absolute w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none opacity-40"
        style={{
          background: 'radial-gradient(circle, #FFDAB9 0%, #E2725B 70%, transparent 90%)',
          top: '5%',
          right: '10%',
          transform: `translate3d(${mousePos.x * -25}px, ${mousePos.y * -25}px, 0)`,
        }}
      />
      <div
        className="absolute w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none opacity-30"
        style={{
          background: 'radial-gradient(circle, #4E2C23 0%, #371D16 80%, transparent 90%)',
          bottom: '5%',
          left: '10%',
          transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)`,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

          {/* ---------- Left: headline and story ---------- */}
          <div className="lg:col-span-7 space-y-6 sm:space-y-8 text-center lg:text-left">

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFFBF7]/15 border border-[#FFDAB9]/40 backdrop-blur-md shadow-sm animate-fade-in">
              <Sparkles className="w-3.5 h-3.5 text-[#FFDAB9]" strokeWidth={1.8} />
              <span className="text-xs uppercase font-extrabold tracking-[0.16em] text-[#FFDAB9]">
                Made by hand, to order
              </span>
            </div>

            {/* Word-by-word reveal. Each word sits in its own overflow mask so
                it rises out of nothing rather than fading in place. */}
            <h1 className="font-90s font-black text-[#FFF7F0] tracking-tight text-4xl sm:text-6xl lg:text-[68px] xl:text-7xl leading-[1.06]">
              {HEADLINE.map((word, i) =>
                i === 0 ? (
                  <MaskedHeading
                    key={word.text}
                    tag="span"
                    text={word.text}
                    src={MASK_WORK.image}
                    srcSet={MASK_SRC}
                    // Seen through 80px-tall letters: the 480 variant is plenty,
                    // and it must never outrank the hero garment for bandwidth.
                    sizes="480px"
                    pointer={mousePos}
                    motionOK={motionOK}
                    // Zoom past the frame edge so only the watercolour wash
                    // shows through the letters — one flat field, no busy edges.
                    fillScale={2}
                    revealDelay={0.25}
                  />
                ) : (
                  <React.Fragment key={word.text}>
                    <span className="word-mask">
                      <span
                        className="apricot-gradient-text font-editorial italic animate-word-reveal"
                        style={{ animationDelay: `${0.25 + i * 0.11}s` }}
                      >
                        {word.text}
                      </span>
                    </span>
                    {i < HEADLINE.length - 1 ? ' ' : ''}
                  </React.Fragment>
                )
              )}
            </h1>

            <p
              className="text-sm sm:text-base lg:text-lg text-[#FFDAB9]/90 max-w-2xl font-normal leading-relaxed mx-auto lg:mx-0 animate-fade-up"
              style={{ animationDelay: '0.7s' }}
            >
              Each piece worked on one at a time, by hand. Specialising in{' '}
              {SERVICES.map((s, i) => (
                <React.Fragment key={s.id}>
                  <strong className="text-white font-bold">{s.name}</strong>
                  {i < SERVICES.length - 2 ? ', ' : i === SERVICES.length - 2 ? ' and ' : '.'}
                </React.Fragment>
              ))}
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 pt-2 animate-fade-up"
              style={{ animationDelay: '0.82s' }}
            >
              {/* One primary action. Browsing is what people do when they are
                  not ready yet, so it does not need a button competing with
                  the one that starts a commission. */}
              <button
                onClick={onOpenQuote}
                className="btn-burnt-peach group w-full sm:w-auto px-8 py-4 text-xs font-extrabold uppercase tracking-wider gap-2.5"
              >
                <Scissors className="w-4 h-4" />
                <span>Start an Order</span>
              </button>

              <button
                onClick={onExplore}
                className="group inline-flex items-center gap-2 text-[#FFDAB9] text-sm font-semibold hover:text-white transition-colors bg-transparent border-0 px-1 py-2"
              >
                <span className="underline underline-offset-4 decoration-[#FFDAB9]/40 group-hover:decoration-white">
                  See the work first
                </span>
                <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </div>

            {/* The stat row used to claim 850+ pieces and a 4.98 rating with
                nothing behind either number. Replaced with what is actually
                true about how the studio works. Put real figures back here
                once they exist. */}
            <div
              className="pt-6 border-t border-white/20 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-xs text-[#FFF7F0]/85 animate-fade-up"
              style={{ animationDelay: '0.94s' }}
            >
              {STUDIO.since && (
                <span className="tabular-nums">Taking orders since {STUDIO.since}</span>
              )}
              <span>Made to order</span>
              <span>Hand-finished in studio</span>
              <span>Quoted per piece</span>
            </div>

          </div>

          {/* ---------- Right: the garment, in depth ---------- */}
          <div className="lg:col-span-5 flex justify-center items-center">
            <div
              style={tiltCardStyle}
              className="relative w-full max-w-[400px] rounded-3xl p-2 bg-[#FFFBF7] border border-[#FFDAB9]/50 shadow-2xl text-[#4E2C23] animate-fade-up"
            >
              <div className="relative rounded-2xl overflow-hidden bg-[#FAF2EB]">
                <div className="relative h-80 sm:h-96 w-full overflow-hidden">

                  <HeroImage layerStyle={layerStyle} />

                  {/* Espresso scrim: gives the caption a ground and warms the
                      frame without touching the fabric colour. */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#4E2C23]/90 via-[#4E2C23]/10 to-transparent" />

                  <div className="absolute top-3 left-3 z-10">
                    <span className="badge-category badge-embroidery shadow-sm">
                      {HERO_CARD.badge}
                    </span>
                  </div>

                  {HERO_CARD.rating && (
                    <div className="absolute top-3 right-3 z-10 px-3 py-1 rounded-full bg-[#FFFBF7]/90 backdrop-blur-md text-xs font-bold text-[#4E2C23] flex items-center gap-1 shadow-sm">
                      <Star className="w-3.5 h-3.5 fill-[#E2725B] text-[#E2725B]" />
                      <span className="tabular-nums">
                        {HERO_CARD.rating.score} ({HERO_CARD.rating.count})
                      </span>
                    </div>
                  )}

                  <div className="absolute bottom-3 left-3 right-3 z-10 space-y-1 text-white">
                    <span className="text-[10px] uppercase tracking-widest text-[#FFDAB9] font-bold">
                      {HERO_CARD.kicker}
                    </span>
                    <h2 className="font-90s text-lg font-bold leading-tight">
                      {HERO_CARD.title}
                    </h2>
                    <div className="flex items-center justify-between gap-3 text-xs text-[#FFDAB9] pt-0.5">
                      <span>{HERO_CARD.materials}</span>
                      <span className="text-white font-bold text-xs whitespace-nowrap">
                        {HERO_CARD.availability}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2.5 p-2.5 rounded-xl bg-[#FAF2EB] flex items-center justify-between text-xs text-[#4E2C23] font-semibold">
                <div className="flex items-center gap-2">
                  <img
                    src="/logo-128.webp"
                    alt=""
                    aria-hidden="true"
                    width="24"
                    height="24"
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span>{HERO_CARD.badge}</span>
                </div>
                <span className="text-[#745147]">{STUDIO.name}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
